// "Compare results": 1–2 picked tests, overlaid on one time axis (each on its own y axis, so different units are
// never put on one scale) or as two panels side by side. Drawn with the shared ECharts host; colours are the
// Signal DS chart tokens (Okabe–Ito blue + orange, colour-blind safe) read from CSS variables.
import { useMemo, useState } from 'react';
import { Button } from '@scripsteam/scrips-signal-ds';
import EChart from '@/analytics-hub/chart/echart';
import {
  compareOption,
  type CompareColors,
  type CompareMode,
  type CompareSeries,
  type LabTrendCardModel,
  type TimelineRange,
} from '@/patients/lib/lab-trend-cards';
import { useCardPoints } from './use-card-points';

const CHART_HEIGHT = 220;

/** Signal DS tokens. A host without the token sheet (tests) reads none, and ECharts uses its own default. */
const TOKENS = {
  first: '--chart-blue',
  second: '--chart-orange',
  axis: '--color-border-default',
  grid: '--color-border-subtle',
  text: '--color-text-muted',
  band: '--color-viz-reference-band',
} as const;

function readTokenColors(): CompareColors {
  const style = typeof document !== 'undefined' ? getComputedStyle(document.documentElement) : null;
  const read = (name: string) => style?.getPropertyValue(name).trim() || undefined;
  return {
    series: [read(TOKENS.first), read(TOKENS.second)],
    axis: read(TOKENS.axis),
    grid: read(TOKENS.grid),
    text: read(TOKENS.text),
    band: read(TOKENS.band),
  };
}

function useCompareSeries(
  patientId: string,
  card: LabTrendCardModel,
  labsByRoot: ReadonlyMap<string, string>,
  range: TimelineRange,
): CompareSeries {
  const { points } = useCardPoints(patientId, card, labsByRoot, range);
  return useMemo(
    () => ({ name: card.row.display, unit: card.row.unit, points, refLow: card.row.refLow, refHigh: card.row.refHigh }),
    [card, points],
  );
}

export interface LabComparePanelProps {
  patientId: string;
  /** The picked tests: 1 or 2. */
  cards: LabTrendCardModel[];
  labsByRoot: ReadonlyMap<string, string>;
  range: TimelineRange;
}

export function LabComparePanel({ patientId, cards, labsByRoot, range }: LabComparePanelProps) {
  const [chosenMode, setMode] = useState<CompareMode>('overlay');
  const [colors] = useState(readTokenColors);
  // Hooks need a fixed count: the second slot reads the first card again when only one is picked.
  const first = useCompareSeries(patientId, cards[0], labsByRoot, range);
  const second = useCompareSeries(patientId, cards[1] ?? cards[0], labsByRoot, range);
  const series = cards.length > 1 ? [first, second] : [first];
  // One test can only be drawn one way.
  const mode: CompareMode = series.length < 2 ? 'overlay' : chosenMode;

  const label = series.map((s) => s.name).join(' and ');
  const empty = series.every((s) => s.points.length === 0);

  return (
    <section data-testid="lab-compare-panel" aria-label="Compare results" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-text-primary">Compare: {label}</h4>
        <div role="group" aria-label="Chart layout" className="flex gap-1">
          <Button
            variant={mode === 'overlay' ? 'secondary' : 'tertiary'}
            size="sm"
            aria-pressed={mode === 'overlay'}
            onClick={() => setMode('overlay')}
          >
            Overlay
          </Button>
          <Button
            variant={mode === 'side-by-side' ? 'secondary' : 'tertiary'}
            size="sm"
            aria-pressed={mode === 'side-by-side'}
            disabled={series.length < 2}
            onClick={() => setMode('side-by-side')}
          >
            Side by side
          </Button>
        </div>
      </div>

      {empty ? (
        <p className="text-xs text-text-muted">No numeric results to plot in this period.</p>
      ) : mode === 'overlay' || series.length < 2 ? (
        <EChart option={compareOption(series, colors)} height={CHART_HEIGHT} ariaLabel={`Trend of ${label}`} />
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {series.map((s, i) => (
            <EChart
              key={`${s.name}-${i}`}
              option={compareOption([s], { ...colors, series: [colors.series[i], colors.series[i]] })}
              height={CHART_HEIGHT}
              ariaLabel={`Trend of ${s.name}`}
            />
          ))}
        </div>
      )}
      <p className="text-xs text-text-hint">Values from different labs, units or ranges are not joined.</p>
    </section>
  );
}
